import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

/**
 * Universal live stream player.
 * Supports:
 *   - HLS (.m3u8) — H.264/AAC via hls.js or native Safari support
 *   - MJPEG — falls back to <img> tag (frame-by-frame)
 * Props:
 *   url: string — playback URL
 *   format: 'hls' | 'mjpeg' (default: auto-detect from URL)
 *   poster: string — optional poster image
 *   className: string
 *   testId: string
 */
export default function LiveStreamPlayer({ url, format, poster, className = "", testId = "live-player", allowScreenshot = false }) {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);

  const detectedFormat = format || (url?.endsWith(".m3u8") ? "hls" : url?.includes(".cgi") || url?.includes("mjpeg") ? "mjpeg" : "hls");

  useEffect(() => {
    if (!url || detectedFormat !== "hls") return;
    const video = videoRef.current;
    if (!video) return;
    let hls;
    if (Hls.isSupported()) {
      hls = new Hls({ lowLatencyMode: true, enableWorker: true });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          setError(`HLS error: ${data.details || data.type}`);
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = url;
    } else {
      setError("Tu navegador no soporta HLS");
    }
    return () => {
      if (hls) hls.destroy();
    };
  }, [url, detectedFormat]);

  if (!url) return <div className="bg-black border border-white/10 aspect-video flex items-center justify-center text-gray-500 text-xs uppercase tracking-wider" data-testid={`${testId}-empty`}>Sin stream configurado</div>;

  if (detectedFormat === "mjpeg") {
    return (
      <img
        src={url}
        alt="Live stream"
        className={`bg-black ${className}`}
        data-testid={`${testId}-mjpeg`}
        onError={() => setError("No se pudo cargar el MJPEG")}
      />
    );
  }

  return (
    <div className={`relative bg-black ${className}`} data-testid={testId}>
      <video
        ref={videoRef}
        controls
        autoPlay
        muted
        playsInline
        poster={poster}
        crossOrigin="anonymous"
        className="w-full h-full"
        data-testid={`${testId}-video`}
      />
      {allowScreenshot && (
        <button
          type="button"
          onClick={() => {
            try {
              const v = videoRef.current;
              if (!v || !v.videoWidth) return;
              const canvas = document.createElement("canvas");
              canvas.width = v.videoWidth;
              canvas.height = v.videoHeight;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(v, 0, 0);
              const data = canvas.toDataURL("image/png");
              const link = document.createElement("a");
              link.href = data;
              link.download = `screenshot-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
              link.click();
            } catch (e) {
              setError("No se pudo capturar (CORS bloqueado)");
            }
          }}
          className="absolute bottom-2 right-2 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-[10px] tracking-widest px-3 py-1.5 transition-colors z-10"
          data-testid={`${testId}-screenshot`}
        >
          📸 Captura
        </button>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-red-400 text-xs px-4 text-center" data-testid={`${testId}-error`}>
          {error}
        </div>
      )}
    </div>
  );
}
